/* The dashboard frame, as `portable/app/CodeNerveApp.jsx` draws it.

   A sticky header carrying the mark, the organisation and its role chip, then a two
   column body: a left rail of icon rows with a 2px indicator that grows from the top on
   the active one, and the screen beside it. Both columns sit inside a 1320px measure.

   Two departures from the prototype, both because the server disagrees with it. Its
   header carries a "14 days left in your free trial" bar; the backend has no trial field,
   and a countdown the server does not know about is a promise the product cannot keep.
   And its organisation name is fixed text, where an account can belong to several.

   The consent dialog appears exactly once. The prototype showed it on every mount and
   discarded the answer, which made it a thing to dismiss rather than a decision; the
   server stores NULL for "never asked", and that is what gates it here. */

import { BarChart3, Brain, GitPullRequest, GitFork, LogOut, Settings as SettingsIcon, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";

import { api } from "@/api";
import { useSession } from "@/session";
import { Button, Chevron, LogoMark, Tag, Toggle, panel } from "@/ui";

const TABS = [
  ["/analytics", "Analytics", BarChart3],
  ["/memory", "Memory", Brain],
  ["/pulls", "Pull requests", GitPullRequest],
  ["/repositories", "Repositories", GitFork],
  ["/settings", "Settings", SettingsIcon],
];

/* Authority over the product, which is not the same as a role inside an organization. */
const PLATFORM_STAFF = new Set(["owner", "admin"]);

const ICON_BUTTON = {
  display: "inline-flex",
  alignItems: "center",
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  color: "var(--text-muted)",
};

function initials(name, email) {
  const source = (name || email || "").trim();
  if (!source) return "CN";
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase();
}

function ConsentDialog({ onDecide }) {
  const [allow, setAllow] = useState(true);
  const [busy, setBusy] = useState(false);

  async function decide() {
    setBusy(true);
    try {
      /* The answer is sent whichever way it goes. Recording only the yes would leave the
         column NULL for anyone who declined, and the prompt would return on every visit. */
      await api.post("/users/me/consent", { learningConsent: allow });
      onDecide(allow);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Learning consent"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        background: "#00000080",
        backdropFilter: "blur(2px)",
        display: "grid",
        placeItems: "center",
        padding: "20px",
        animation: "cn-fade-up 320ms var(--ease-out) both",
      }}
    >
      <div
        style={{
          ...panel,
          width: "min(420px,94vw)",
          borderRadius: "16px",
          boxShadow: "0 60px 120px -50px #000",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "20px 22px 0" }}>
          <h2
            style={{
              font: "500 18px/1.2 var(--font-display)",
              letterSpacing: "-0.02em",
              color: "var(--text-primary)",
              marginBottom: "14px",
            }}
          >
            Help us improve CodeNerve
          </h2>
          <p style={{ font: "var(--text-body-14)", color: "var(--text-secondary)", margin: 0 }}>
            CodeNerve can use your reactions to review comments to improve the reviewer for
            your organisation. Your code is never used to train a model. You can change this
            any time in Settings, or write to security@codenerve.ai.
          </p>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              margin: "18px 0",
              font: "var(--text-body-14)",
              color: "var(--text-primary)",
            }}
          >
            <Toggle checked={allow} onChange={setAllow} label="Allow learning from usage" />
            Allow learning from usage
          </label>
        </div>
        <div style={{ padding: "0 22px 22px" }}>
          <Button disabled={busy} onClick={decide} style={{ width: "100%", justifyContent: "center" }}>
            {busy ? "Saving…" : "Continue"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function Shell() {
  const { user, profile, organization, organizations, selectOrganization, signOut } = useSession();
  const [consentAnswered, setConsentAnswered] = useState(
    () => profile?.learningConsent !== null && profile?.learningConsent !== undefined,
  );
  const tabs = PLATFORM_STAFF.has(user?.role)
    ? [...TABS, ["/admin", "Platform admin", ShieldCheck]]
    : TABS;

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
      {!consentAnswered && <ConsentDialog onDecide={() => setConsentAnswered(true)} />}

      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 40,
          background: "var(--bg)",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "14px",
            maxWidth: "1320px",
            margin: "0 auto",
            padding: "12px clamp(14px,2vw,26px) 0",
          }}
        >
          <LogoMark size={20} color="var(--logo-ink)" />

          <span style={{ display: "inline-flex", alignItems: "center", gap: "9px", minWidth: 0 }}>
            {organizations.length > 1 ? (
              <select
                value={organization?.id ?? ""}
                onChange={(event) => selectOrganization(event.target.value)}
                aria-label="Organisation"
                style={{
                  appearance: "none",
                  WebkitAppearance: "none",
                  font: "var(--text-body-14)",
                  color: "var(--text-primary)",
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                {organizations.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            ) : (
              <span style={{ font: "var(--text-body-14)", color: "var(--text-primary)" }}>
                {organization?.name}
              </span>
            )}
            {organization && <Tag>{organization.role === "org_admin" ? "Admin" : "Member"}</Tag>}
            {organizations.length > 1 && (
              <span aria-hidden="true" style={{ color: "var(--text-faint)" }}>
                <Chevron />
              </span>
            )}
          </span>

          <span style={{ marginLeft: "auto", display: "flex", gap: "12px", alignItems: "center" }}>
            <button type="button" onClick={signOut} title="Sign out" style={ICON_BUTTON}>
              <LogOut size={16} />
            </button>
            <span
              title={user?.email}
              style={{
                display: "grid",
                placeItems: "center",
                width: "26px",
                height: "26px",
                borderRadius: "999rem",
                background: "var(--surface-elevated)",
                border: "1px solid var(--border-strong)",
                boxSizing: "border-box",
                font: "10px/1 var(--font-mono)",
                color: "var(--text-primary)",
              }}
            >
              {initials(profile?.name, user?.email)}
            </span>
          </span>
        </div>
        <div style={{ height: "12px" }} />
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0,196px) minmax(0,1fr)",
          gap: "clamp(14px,2vw,28px)",
          maxWidth: "1320px",
          margin: "0 auto",
          padding: "clamp(16px,2.4vw,26px) clamp(14px,2vw,26px) 60px",
          alignItems: "start",
        }}
      >
        <nav style={{ position: "sticky", top: "84px", display: "grid", gap: "3px" }}>
          {tabs.map(([to, label, Icon]) => to === "/memory" ? (
            // Off until memory is shown not to lower the benchmarked review quality.
            <span
              key={to}
              aria-disabled="true"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "11px",
                padding: "11px 13px",
                font: "var(--text-body-14)",
                color: "var(--text-disabled)",
                cursor: "not-allowed",
              }}
            >
              <Icon size={15} style={{ flex: "0 0 auto" }} />
              {label}
              <Tag>Soon</Tag>
            </span>
          ) : (
            <NavLink
              key={to}
              to={to}
              style={({ isActive }) => ({
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: "11px",
                textDecoration: "none",
                borderRadius: "10px",
                padding: "11px 13px",
                font: "var(--text-body-14)",
                color: isActive ? "var(--text-primary)" : "var(--text-muted)",
                background: isActive ? "var(--surface-elevated)" : "transparent",
                transition: "background 260ms var(--ease-out), color 260ms var(--ease-out)",
              })}
            >
              {({ isActive }) => (
                <>
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: 0,
                      top: "10px",
                      bottom: "10px",
                      width: "2px",
                      borderRadius: "999rem",
                      background: "var(--text-primary)",
                      transformOrigin: "top",
                      transform: isActive ? "scaleY(1)" : "scaleY(0)",
                      transition: "transform 300ms var(--ease-out)",
                    }}
                  />
                  <Icon size={15} style={{ flex: "0 0 auto" }} />
                  {label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <main style={{ minWidth: 0, display: "grid", gap: "clamp(14px,1.6vw,24px)" }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
